import { useMutation } from '@apollo/client';

import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input,
  VStack,
  useToast,
  ButtonGroup,
} from '@chakra-ui/react';

import { Select } from "chakra-react-select";

import moment from 'moment';

import { useState } from 'react';

import { useForm, Controller } from 'react-hook-form';

import {
  ADD_NEWSLETTER,
  UPDATE_NEWSLETTER,
  DELETE_NEWSLETTER,
  QUERY_PUBLISHED_NEWSLETTERS,
} from '../../../../Queries';

import { useUser } from '../../../../Context';

const documentTypeOptions = [
  { label: 'Board Minutes', value: 'BOARD_MINUTES' },
  { label: 'Financial Statements', value: 'FINANCIAL_STATEMENTS' },
  { label: 'Membership Materials', value: 'MEMBERSHIP_MATERIALS' },
  { label: 'Other Documents', value: 'OTHER_DOCUMENTS' },
];

function generateId() {
  return 'doc-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 8);
}

export const AddNewsletterModal = ({ isOpen, onClose }) => {
  const { user } = useUser();
  const toast = useToast();

  const [isWorking, setIsWorking] = useState(false);

  const defaultValues = {
    newsletter_name: '',
    newsletter_date: moment().unix(),
    newsletter_file: null,
    document_type: '',
  };

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues
  });

  const clearForm = () => {
    setValue('newsletter_name', defaultValues?.newsletter_name);
    setValue('newsletter_date', moment().unix());
    setValue('newsletter_file', defaultValues?.newsletter_file);
    setValue('document_type', defaultValues?.document_type);
  };

  const formData = watch();

  const [addNewsletter] = useMutation(ADD_NEWSLETTER);
  const [updateNewsletter] = useMutation(UPDATE_NEWSLETTER);
  const [deleteNewsletter] = useMutation(DELETE_NEWSLETTER);

  const errorToast = (description = '') => {
    toast({
      title: 'Document not added.',
      description,
      status: 'error',
      duration: 4000,
      isClosable: true,
    });
  };

  // Upload the selected PDF to S3 using the presigned POST fields.
  // Returns true on success, false on any failure (so the caller can roll back).
  const uploadFile = async (uploadUrl, uploadFields) => {
    const formDataObject = new FormData();

    formDataObject.append('key', uploadFields?.key);
    formDataObject.append('policy', uploadFields?.policy);
    formDataObject.append('x-amz-algorithm', uploadFields['x-amz-algorithm']);
    formDataObject.append('x-amz-credential', uploadFields['x-amz-credential']);
    formDataObject.append('x-amz-date', uploadFields['x-amz-date']);
    formDataObject.append('x-amz-security-token', uploadFields['x-amz-security-token']);
    formDataObject.append('x-amz-signature', uploadFields['x-amz-signature']);
    formDataObject.append('file', formData.newsletter_file);

    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formDataObject,
      });
      return response.ok;
    } catch (e) {
      return false;
    }
  };

  // Transactional create:
  // 1. Create the record as DRAFT (not visible to members).
  // 2. Upload the PDF to S3.
  // 3. Only if the upload succeeds, flip the record to PUBLISHED.
  // 4. If the upload fails, delete the DRAFT record so no orphan is left behind.
  const onSubmit = async () => {
    const newsletterId = generateId();
    setIsWorking(true);

    // Step 1: create as DRAFT and get the presigned upload URL.
    let addResult;
    try {
      addResult = await addNewsletter({
        variables: {
          newsletter_details: {
            newsletter_status: 'DRAFT',
            newsletter_name: formData?.newsletter_name,
            newsletter_date: formData?.newsletter_date,
            newsletter_published_date: moment().unix(),
            newsletter_published_by: user?.username,
            document_type: formData?.document_type || null,
          },
          newsletter_id: newsletterId,
        },
      });
    } catch (e) {
      setIsWorking(false);
      errorToast();
      return;
    }

    const uploadUrlData = addResult?.data?.addNewsletter?.newsletter_details?.newsletter_upload_url;
    const uploadUrl = uploadUrlData?.url;
    let uploadFields;
    try {
      uploadFields = JSON.parse(uploadUrlData?.fields);
    } catch (e) {
      uploadFields = null;
    }

    if (!uploadUrl || !uploadFields) {
      // Could not get an upload target: remove the DRAFT record and bail.
      await deleteNewsletter({ variables: { newsletter_id: newsletterId } }).catch(() => {});
      setIsWorking(false);
      errorToast();
      return;
    }

    // Step 2: upload the PDF.
    const uploaded = await uploadFile(uploadUrl, uploadFields);

    if (!uploaded) {
      // Step 4 (failure path): roll back the DRAFT record so it never orphans.
      await deleteNewsletter({ variables: { newsletter_id: newsletterId } }).catch(() => {});
      setIsWorking(false);
      errorToast('The file could not be uploaded. Please try again.');
      return;
    }

    // Step 3: upload succeeded, publish the record.
    try {
      await updateNewsletter({
        variables: {
          newsletter_details: {
            newsletter_status: 'PUBLISHED',
            newsletter_name: formData?.newsletter_name,
            newsletter_date: formData?.newsletter_date,
            newsletter_published_date: moment().unix(),
            newsletter_published_by: user?.username,
            document_type: formData?.document_type || null,
          },
          newsletter_id: newsletterId,
        },
        refetchQueries: [QUERY_PUBLISHED_NEWSLETTERS],
      });
    } catch (e) {
      // The file is in S3 but publishing failed. Leave the DRAFT record in place
      // (it is not member-visible) rather than deleting a record whose file exists.
      setIsWorking(false);
      errorToast('The document was uploaded but could not be published. Please edit it and set status to Published.');
      return;
    }

    toast({
      title: 'Document added.',
      description: '',
      status: 'success',
      duration: 4000,
      isClosable: true,
    });
    setIsWorking(false);
    onClose();
    clearForm();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Document</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack mb={2} as="form" alignItems="start" onSubmit={handleSubmit(onSubmit)}>
              <FormControl isInvalid={errors.newsletter_name}>
                <FormLabel htmlFor='newsletter_name'>Document name *</FormLabel>
                <Input
                  id='newsletter_name'
                  placeholder='document name'
                  {...register('newsletter_name', {
                    required: 'This is required',
                    minLength: { value: 4, message: 'Minimum length should be 4' },
                  })}
                />
                <FormErrorMessage>
                  {errors.newsletter_name && errors.newsletter_name.message}
                </FormErrorMessage>
              </FormControl>
              <Controller
                control={control}
                name="document_type"
                rules={{ required: "This is required" }}
                render={({
                  field: { onChange, onBlur, value, name, ref },
                }) => (
                  <FormControl isInvalid={errors.document_type} id="document_type">
                    <FormLabel>Document type *</FormLabel>
                    <Select
                      ref={ref}
                      name={name}
                      onBlur={onBlur}
                      onChange={(value) => onChange(value.value)}
                      options={documentTypeOptions}
                      placeholder="select document type"
                      value={documentTypeOptions.find((option) => option.value === value)}
                    />
                    <FormErrorMessage>{errors.document_type && errors.document_type.message}</FormErrorMessage>
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="newsletter_date"
                rules={{ required: "This is required" }}
                render={({
                  field: { onChange, onBlur, value, name, ref },
                }) =>  (
                  <FormControl id="newsletter_date" isInvalid={errors.newsletter_date}>
                    <FormLabel>Document date *</FormLabel>
                    <Input
                      type="date"
                      name={name}
                      value={value ? moment.utc(value * 1000).format('yyyy-MM-DD') : undefined}
                      onChange={(event) => onChange(event.target.value ? moment(event.target.value).unix(): "")}
                      id='newsletter_date'
                      placeholder='document date'
                    />
                    <FormErrorMessage>
                      {errors.newsletter_date && errors.newsletter_date.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="newsletter_file"
                rules={{ required: "This is required" }}
                render={({
                  field: { onChange, onBlur, value, name, ref },
                }) =>  { return (
                <FormControl isInvalid={errors.newsletter_file}>
                  <FormLabel htmlFor='newsletter_file'>Document file (.pdf) *</FormLabel>
                  <Input
                    name={name}
                    type='file'
                    id='newsletter_file'
                    accept='application/pdf'
                    placeholder='document file'
                    value={value?.fileName}
                    onChange={(event) => {
                      onChange(event.target.files[0]);
                    }}
                  />
                  <FormErrorMessage>
                    {errors.newsletter_file && errors.newsletter_file.message}
                  </FormErrorMessage>
                </FormControl>
                )}}
              />
              <ButtonGroup pt={2}>
                <Button
                  type='submit'
                  colorScheme='teal'
                  isLoading={isSubmitting || isWorking}
                  isDisabled={isSubmitting || isWorking}
                >
                  Submit
                </Button>
                <Button onClick={onClose}>Cancel</Button>
              </ButtonGroup>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
